import { Trans, useTranslation } from '@mc/i18n'
import { useStaticData } from '@mc/static-data-react'
import type { GrowthCategory } from '@mc/api'

import { Course } from 'types/data'
import {
  getGrowthClassCategories,
  PrimaryCategoryAndSubcategory,
} from 'libs/server/api'
import { getChaptersCount, getCourseTime } from '../utils'

import { SubcatPill } from './SubcatPill'
import { ClassSpecialType, ClassSpecialTypes } from '../../classSpecialType'

export const AboutSection = () => {
  const {
    course,
    classSubcategories,
    classSpecialType,
    primaryGrowthCatAndSubcat,
    classCategories,
  } = useStaticData<{
    course: Course
    classSubcategories: GrowthCategory[]
    classSpecialType: ClassSpecialType
    primaryGrowthCatAndSubcat: PrimaryCategoryAndSubcategory
    classCategories: Awaited<ReturnType<typeof getGrowthClassCategories>>
  }>()

  const { t } = useTranslation('@mc/next')

  const shouldShowTopicPills = classSubcategories.length > 0

  // Continuous cut
  const hasSubchapters = course.sortedSubchapters?.length
  const chapters = hasSubchapters
    ? course.sortedSubchapters
    : course.sortedChapters

  const courseDuration = getCourseTime(chapters, hasSubchapters)
  const chaptersCount = getChaptersCount(chapters)

  let lengthI18n = 'course.details.length'
  if (classSpecialType === ClassSpecialTypes.DOCUMENTARY) {
    lengthI18n = 'documentary.details.length'
  } else if (hasSubchapters) {
    lengthI18n = 'course.details.lengthCC'
  }

  const categoriesLabel = classCategories.map((cat) => cat.name).join(', ')

  return (
    <div className='col-md-8'>
      <p className='mc-text-small mc-opacity--muted'>{course.overview}</p>
      <p className='mc-mt-6 mc-mb-2'>
        <Trans
          i18nKey={
            classSpecialType === ClassSpecialTypes.DOCUMENTARY
              ? 'documentary.details.film_title'
              : 'course.details.instructors'
          }
          values={{ instructorName: course.instructorName }}
          ns='@mc/next'
        >
          <span className='mc-text-small mc-opacity--muted' />
          <span className='mc-text-h7' />
        </Trans>
      </p>
      <p className='mc-mb-2'>
        <Trans
          i18nKey={lengthI18n}
          values={{ count: chaptersCount, courseDuration }}
          ns='@mc/next'
        >
          <span className='mc-text-small mc-opacity--muted' />
          <span className='mc-text-h7' />
        </Trans>
      </p>
      <p>
        <Trans
          i18nKey='course.details.category'
          values={{ categories: categoriesLabel }}
          ns='@mc/next'
        >
          <span className='mc-text-small mc-opacity--muted' />
          <span className='mc-text-h7' />
        </Trans>
      </p>
      {shouldShowTopicPills && (
        <p className='d-inline-flex flex-wrap'>
          <span className='mc-text-small mc-opacity--muted mc-mr-3 mc-mt-2'>
            {t('course.details.topics')}
          </span>
          {classSubcategories.map((subcat) => (
            <SubcatPill
              key={subcat.slug}
              categoryName={primaryGrowthCatAndSubcat?.primaryCategory?.name}
              subcatName={subcat.name}
              subcatSlug={subcat.slug}
            />
          ))}
        </p>
      )}
    </div>
  )
}
